import { FinanceContext, type FinancePageContext } from "@/features/finance/model/finance-tabs"
import { FinanceGovernance } from "@/features/finance/types/finance-governance"

/**
 * The ONE answer to "where am I and what may I show" for every finance surface.
 *
 * Every finance bug this quarter was the same shape: a widget, a page or a service worked out its own context
 * and worked it out differently. `$0.00` for a withheld branch, widgets removed instead of degraded, a
 * consolidated board that never fetched its governance, a parish that could not see its own bank account, four
 * copies of `toDateInputValue` — none were careless. Each author had to re-derive a fact nobody had written
 * down in one place.
 *
 * So this composes the existing sources rather than becoming a fifth one:
 *
 *  - SHAPE comes from `useShowMultiBranchWorkspaceUi` (`workspace.structureType`, never a branch count);
 *  - SCOPE comes from the page's own context (branch / parish / global);
 *  - GOVERNANCE and DISCLOSURE come from the SERVER, on the finance-setup status.
 *
 * The last one is the important one. Disclosure is not the client's to compute: "may I see this branch's
 * money" is answered by the permission graph, so the client asks and the server tells. A UI that decided for
 * itself would be guessing, and guessing is what produced the zeroes.
 */

/** Whose books this surface is about. */
export enum FinanceScope {
  /** One operational branch — its own books. */
  Branch = "branch",
  /** The parish AS a branch: its own transactions, accounts and reconciliations. */
  Parish = "parish",
  /** The whole workspace, consolidated across every branch plus the parish. */
  Global = "global",
}

/** The workspace's shape. Mono is a branch, not a parish — ADR-0025. */
export enum FinanceStructure {
  SingleBranch = "single_branch",
  MultiBranch = "multi_branch",
}

/** Whether the reader holds this branch's books, or is looking in from outside. */
export enum FinanceOwnership {
  /** The reader's own branch, or the parish reading the parish. Absolutes are theirs by right. */
  Own = "own",
  /** Someone else's branch. What travels back depends on governance. */
  Foreign = "foreign",
}

/** What the figures on this surface ARE. */
export enum FinanceDisclosureLevel {
  /** Real amounts, in the scope's currency. */
  Absolute = "absolute",
  /** Shares of a group total, in percent. Never render a currency beside these. */
  Shares = "shares",
}

export type FinanceSurfaceContext = {
  scope: FinanceScope
  structure: FinanceStructure
  governance: FinanceGovernance
  ownership: FinanceOwnership
  disclosure: FinanceDisclosureLevel
  /** True while the server has not yet answered. Treat as "assume nothing" rather than as a default. */
  isResolving: boolean
}

const SCOPE_BY_PAGE_CONTEXT: Record<FinancePageContext, FinanceScope> = {
  [FinanceContext.Branch]: FinanceScope.Branch,
  [FinanceContext.Parish]: FinanceScope.Parish,
  [FinanceContext.Global]: FinanceScope.Global,
}

/** What the server said about this reader, from the finance setup status. Undefined until it answers. */
export type FinanceDisclosureFacts = {
  /** May this caller see the absolute figures of the branch the page is scoped to? */
  branchAbsolutes: boolean
  /** May this caller see EVERY active branch's — what an honest consolidated total requires? */
  allBranchAbsolutes: boolean
}

/**
 * The rule itself, with no React in it, so every case can be tested directly — including the ones that only
 * differ by which of two booleans the server sent.
 */
export function resolveFinanceSurfaceContext(input: {
  pageContext: FinancePageContext
  governance: FinanceGovernance
  showMultiBranchUi: boolean
  disclosure: FinanceDisclosureFacts | undefined
  isLoading: boolean
}): FinanceSurfaceContext {
  const scope = SCOPE_BY_PAGE_CONTEXT[input.pageContext] ?? FinanceScope.Branch

  /*
   * A consolidated total needs EVERY branch to be honest — with n-1 of n disclosable the last is recoverable
   * by subtraction — so Global asks the harder question than a single branch's page does.
   */
  const maySeeAbsolutes =
    scope === FinanceScope.Global ? input.disclosure?.allBranchAbsolutes : input.disclosure?.branchAbsolutes

  return {
    scope,
    structure: input.showMultiBranchUi ? FinanceStructure.MultiBranch : FinanceStructure.SingleBranch,
    governance: input.governance,
    /*
     * Ownership is derived from what the server DISCLOSED, not from the branch id and not from the mode. That
     * is what it means here: a reader holds this branch's books exactly when its absolutes travel back to
     * them. Deriving it from governance alone would blind a branch treasurer to their own branch.
     */
    ownership: maySeeAbsolutes === false ? FinanceOwnership.Foreign : FinanceOwnership.Own,
    disclosure: maySeeAbsolutes === false ? FinanceDisclosureLevel.Shares : FinanceDisclosureLevel.Absolute,
    isResolving: input.isLoading || input.disclosure === undefined,
  }
}
